/**
 * ERPEditableGrid — Inline-editable data grid for ERP transaction screens
 * Features: Add row, delete row, inline cell editing, Enter-tab navigation, totals footer
 * Uses: erp-theme.css classes only
 */
import React, { useState, useCallback, useRef } from 'react';
import { Plus, Trash2, Search, ChevronDown } from 'lucide-react';
import '../../styles/erp-theme.css';

const ERPEditableGrid = ({
  columns,       // [{ field, header, width?, type?, options?, readOnly?, required?, lookup? }]
  rows,          // array of row objects
  onChange,       // (newRows) => void
  onLookup,      // (rowIndex, field) => void — opens lookup for a cell
  idField,       // primary key field
  testId = 'editable-grid',
  showAddRow = true,
  showDeleteRow = true,
  showRowNumbers = true,
  maxRows = 50,
  emptyRow,      // template for new rows
  footerSummary, // [{ field, type: 'sum'|'count'|'label', label? }]
}) => {
  const [editingCell, setEditingCell] = useState(null); // {row, col}
  const tableRef = useRef(null);

  const addRow = useCallback(() => {
    if (rows.length >= maxRows) return;
    const newRow = emptyRow ? { ...emptyRow, _rowId: Date.now() } : { _rowId: Date.now() };
    onChange([...rows, newRow]);
    // Focus first editable cell of new row after render
    setTimeout(() => {
      const firstEditable = columns.find(c => !c.readOnly);
      if (firstEditable) setEditingCell({ row: rows.length, col: firstEditable.field });
    }, 50);
  }, [rows, onChange, columns, emptyRow, maxRows]);

  const deleteRow = useCallback((idx) => {
    const next = [...rows]; next.splice(idx, 1);
    onChange(next);
  }, [rows, onChange]);

  const updateCell = useCallback((rowIdx, field, value) => {
    const next = [...rows];
    next[rowIdx] = { ...next[rowIdx], [field]: value };
    onChange(next);
  }, [rows, onChange]);

  // Enter/Tab navigation
  const handleKeyDown = useCallback((e, rowIdx, colIdx) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const editableCols = columns.filter(c => !c.readOnly);
      const nextColIdx = editableCols.findIndex(c => c.field === columns[colIdx]?.field) + 1;
      if (nextColIdx < editableCols.length) {
        setEditingCell({ row: rowIdx, col: editableCols[nextColIdx].field });
      } else if (rowIdx < rows.length - 1) {
        setEditingCell({ row: rowIdx + 1, col: editableCols[0]?.field });
      } else if (showAddRow && rows.length < maxRows) {
        addRow();
      }
    }
    if (e.key === 'Escape') setEditingCell(null);
  }, [columns, rows, addRow, showAddRow, maxRows]);

  // Compute footer
  const computeFooter = () => {
    if (!footerSummary) return null;
    return footerSummary.map(fs => {
      if (fs.type === 'sum') {
        const total = rows.reduce((s, r) => s + (parseFloat(r[fs.field]) || 0), 0);
        return { ...fs, value: total.toLocaleString('en-IN', { minimumFractionDigits: 2 }) };
      }
      if (fs.type === 'count') return { ...fs, value: rows.length };
      return { ...fs, value: fs.label || '' };
    });
  };
  const footer = computeFooter();

  const renderCell = (row, col, rowIdx, colIdx) => {
    const isEditing = editingCell?.row === rowIdx && editingCell?.col === col.field;
    const value = row[col.field] ?? '';

    if (col.readOnly) {
      return <span style={{ fontSize: 11 }}>{value}</span>;
    }

    if (isEditing) {
      if (col.type === 'select' && col.options) {
        return (
          <select value={value} autoFocus
            onChange={e => updateCell(rowIdx, col.field, e.target.value)}
            onBlur={() => setEditingCell(null)}
            onKeyDown={e => handleKeyDown(e, rowIdx, colIdx)}
            className="erp-input erp-select" style={{ fontSize: 11, padding: '2px 4px', height: 26 }}>
            <option value="">--</option>
            {col.options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
          </select>
        );
      }
      return (
        <input type={col.type === 'number' ? 'number' : 'text'} value={value} autoFocus
          onChange={e => updateCell(rowIdx, col.field, e.target.value)}
          onBlur={() => setEditingCell(null)}
          onKeyDown={e => handleKeyDown(e, rowIdx, colIdx)}
          className="erp-input" style={{ fontSize: 11, padding: '2px 6px', height: 26, textAlign: col.type === 'number' ? 'right' : 'left', fontFamily: col.type === 'number' ? 'var(--erp-font-mono)' : 'inherit' }}
          data-testid={`${testId}-cell-${rowIdx}-${col.field}`} />
      );
    }

    // Display mode — click to edit
    return (
      <div
        onClick={() => setEditingCell({ row: rowIdx, col: col.field })}
        style={{ cursor: 'text', minHeight: 20, fontSize: 11, padding: '2px 4px', display: 'flex', alignItems: 'center', gap: 4, textAlign: col.type === 'number' ? 'right' : 'left', fontFamily: col.type === 'number' ? 'var(--erp-font-mono)' : 'inherit', width: '100%', justifyContent: col.type === 'number' ? 'flex-end' : 'flex-start' }}
        data-testid={`${testId}-display-${rowIdx}-${col.field}`}
      >
        {value || <span style={{ color: 'var(--erp-text-muted)', fontSize: 10 }}>—</span>}
        {col.lookup && (
          <button onClick={e => { e.stopPropagation(); onLookup?.(rowIdx, col.field); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--erp-primary)', flexShrink: 0 }}>
            <Search size={10} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="erp-grid-wrapper" data-testid={testId} ref={tableRef}>
      <div className="erp-grid-scroll" style={{ maxHeight: 320 }}>
        <table className="erp-grid" style={{ minWidth: 'unset' }}>
          <thead>
            <tr>
              {showRowNumbers && <th style={{ width: 36, textAlign: 'center' }}>#</th>}
              {columns.map(c => (
                <th key={c.field} style={{ width: c.width, minWidth: c.width || 80 }}>
                  {c.header}{c.required && <span style={{ color: '#e74c3c', marginLeft: 2 }}>*</span>}
                </th>
              ))}
              {showDeleteRow && <th style={{ width: 36, textAlign: 'center' }}>Del</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length + (showRowNumbers ? 1 : 0) + (showDeleteRow ? 1 : 0)} style={{ textAlign: 'center', padding: 16, color: 'var(--erp-text-muted)', fontSize: 11 }}>
                No rows. {showAddRow && 'Click + to add.'}
              </td></tr>
            ) : rows.map((row, rowIdx) => (
              <tr key={row._rowId || row[idField] || rowIdx} style={{ background: editingCell?.row === rowIdx ? 'var(--erp-grid-row-selected)' : undefined }}>
                {showRowNumbers && <td style={{ textAlign: 'center', fontSize: 10, color: 'var(--erp-text-muted)' }}>{rowIdx + 1}</td>}
                {columns.map((col, colIdx) => (
                  <td key={col.field} style={{ padding: '2px 4px' }}>
                    {renderCell(row, col, rowIdx, colIdx)}
                  </td>
                ))}
                {showDeleteRow && (
                  <td style={{ textAlign: 'center' }}>
                    <button onClick={() => deleteRow(rowIdx)} className="erp-btn-icon delete" style={{ padding: 2 }} title="Remove row" data-testid={`${testId}-del-${rowIdx}`}>
                      <Trash2 size={12} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer: Add Row + Summary */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px', borderTop: '1px solid var(--erp-border)', background: 'var(--erp-surface-alt)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {showAddRow && rows.length < maxRows && (
            <button onClick={addRow} className="erp-btn erp-btn-primary" style={{ padding: '3px 10px', fontSize: 10 }} data-testid={`${testId}-add-row`}>
              <Plus size={12} /> Add Row
            </button>
          )}
          <span style={{ fontSize: 10, color: 'var(--erp-text-muted)' }}>{rows.length} row(s)</span>
        </div>
        {footer && (
          <div style={{ display: 'flex', gap: 16 }}>
            {footer.map((f, i) => (
              <span key={i} style={{ fontSize: 10, fontWeight: 600 }}>
                {f.header || f.field}: <span style={{ fontFamily: 'var(--erp-font-mono)', color: 'var(--erp-primary)' }}>{f.value}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ERPEditableGrid;
